/**
 * 视频任务节点 - 展示视频生成任务的状态和结果
 * 自动轮询更新任务状态，展示生成的视频
 */
import React, { memo, useState, useEffect, useRef } from 'react';
import { useReactFlow } from '@xyflow/react';
import { nanoid } from 'nanoid';
import { NodeLayout } from './NodeLayout';
import { useProject } from '../providers/ProjectProvider';
import { useNodeOperations } from '../providers/NodeOperationsProvider';
import ApiService from '../../../api/ApiService';
import LocalDataService from '../../../api/LocalDataService';
import { toast } from 'react-toastify';
import './VideoTaskNode.css';

const apiService = new ApiService();

export const VideoTaskNode = memo(({ id, data }) => {
  const { updateNodeData, getNode } = useReactFlow();
  const { project, readOnly } = useProject();
  const { addNodesWithEdges } = useNodeOperations();
  const [status, setStatus] = useState(data.status || 'pending');
  const [videoUrl, setVideoUrl] = useState(data.videoUrl || null);
  const [error, setError] = useState(data.error || null);
  const [inputParams, setInputParams] = useState(data.inputParams || {});
  const [outputParams, setOutputParams] = useState(data.outputParams || null);
  const [isTransferring, setIsTransferring] = useState(false); // 视频转存状态
  const [extracting, setExtracting] = useState(false); // 帧提取状态
  const [extractingType, setExtractingType] = useState(null); // 提取的帧类型
  const pollingIntervalRef = useRef(null);
  const mountedRef = useRef(true);
  const hasCheckedTransferRef = useRef(false);
  const previousTransferStatusRef = useRef(null);
  const videoRef = useRef(null); // 视频元素引用
  const thumbnailUpdateTriggeredRef = useRef(false); // 每个任务只触发一次视频首帧封面更新

  const taskId = data.taskId;
  const taskType = data.taskType || 'text-to-video'; // 'text-to-video' 或 'image-to-video'
  const apiTaskType = data.apiTaskType || null; // 新版模型使用 'v3_api'

  // 视频节点：转存完成后会用首帧更新项目封面（仅当项目尚无封面时）；图片节点同样会更新封面

  // 轮询查询任务状态
  const pollTaskStatus = async () => {
    if (!taskId || !mountedRef.current) return;

    try {
      let result;
      if (apiTaskType === 'v3_api') {
        const v3Result = await apiService.queryV3Task(taskId);
        result = {
          ...v3Result,
          data: v3Result?.data ? {
            ...v3Result.data,
            input_params: v3Result.data.params || {},
            output_params: v3Result.data.output || v3Result.data.result || null,
          } : null,
        };
      } else {
        const apiEndpoint = taskType === '视频换脸'
          ? `${apiService.apiBase}/v2/tasks/info`
          : `${apiService.apiBase}/fal/tasks/info`;
        result = await apiService.request(
          apiEndpoint,
          'POST',
          { task_id: taskId }
        );
      }

      if (!mountedRef.current) return;

      if (result.code === 200 && result.data) {
        const newStatus = result.data.status;
        setStatus(newStatus);
        setInputParams(result.data.input_params || {});
        setOutputParams(result.data.output_params || null);

        if (newStatus === 'completed') {
          // 任务完成，提取视频URL
          // 支持两种格式：
          // 1. output_params.video.url (FAL API)
          // 2. output_params.video_url (V2 API)
          const outputData = result.data.output_params || {};
          const videoData = outputData.video;
          const v2VideoUrl = outputData.video_url;
          
          let extractedVideoUrl = null;
          if (typeof videoData === 'string') {
            extractedVideoUrl = videoData;
          } else if (videoData && videoData.url) {
            extractedVideoUrl = videoData.url;
          } else if (v2VideoUrl) {
            extractedVideoUrl = v2VideoUrl;
          } else if (typeof outputData.url === 'string') {
            extractedVideoUrl = outputData.url;
          } else if (typeof outputData.output === 'string') {
            extractedVideoUrl = outputData.output;
          } else if (outputData.output?.url) {
            extractedVideoUrl = outputData.output.url;
          }
          
          if (extractedVideoUrl) {
            setVideoUrl(extractedVideoUrl);
            setError(null);
            
            // 检查视频是否已经转存到自己的服务器
            const isTransferred =
              extractedVideoUrl.startsWith('https://st-video.cc') ||
              extractedVideoUrl.startsWith('https://cdn-video.51sux.com') ||
              extractedVideoUrl.startsWith('https://cdn-hk.51sux.com');
            
            hasCheckedTransferRef.current = true;
            
            if (isTransferred) {
              // 视频已转存，停止轮询
              console.log(`VideoTaskNode ${id} - 视频已转存到 st-video.cc，停止轮询`);
              setIsTransferring(false);
              if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
              }

              // 视频已转存：用首帧更新项目封面（每个任务最多一次，且只在项目尚无封面时）
              if (!thumbnailUpdateTriggeredRef.current) {
                thumbnailUpdateTriggeredRef.current = true;
                updateProjectThumbnailFromVideo(extractedVideoUrl);
              }
              previousTransferStatusRef.current = true;
            } else {
              // 视频还未转存，继续轮询但降低频率到30秒
              console.log(`VideoTaskNode ${id} - 视频还未转存，继续30秒轮询`);
              setIsTransferring(true);
              previousTransferStatusRef.current = false;
              
              if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
              }
              // 设置30秒轮询
              pollingIntervalRef.current = setInterval(() => {
                pollTaskStatus();
              }, 30000);
            }
          }
        } else if (newStatus === 'failed') {
          const outputError = result.data.output_params?.error;
          const failureMessage = result.data.error
            || (typeof outputError === 'string' ? outputError : outputError?.message)
            || result.data.output_params?.message
            || '生成失败';
          setError(failureMessage);
          
          // 停止轮询
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        }
      }
    } catch (err) {
      console.error('查询视频任务状态失败:', err);
    }
  };

  // 同步状态到节点数据
  useEffect(() => {
    updateNodeData(id, {
      status,
      videoUrl,
      error,
      inputParams,
      outputParams,
    });
    console.log(`VideoTaskNode ${id} - 更新节点数据:`, { status, videoUrl });
  }, [id, status, videoUrl, error, inputParams, outputParams, updateNodeData]);

  // 检查视频是否需要转存
  const checkVideoTransfer = (url) => {
    if (!url) return false;
    return !url.startsWith('https://st-video.cc')
      && !url.startsWith('https://cdn-video.51sux.com')
      && !url.startsWith('https://cdn-hk.51sux.com');
  };

  // 检查视频是否支持 CORS（用于帧提取）
  const isVideoCorsSafe = (url) => {
    if (!url) return false;
    // 转存到 st-video.cc 或 cdn-video.51sux.com 的视频都支持 CORS
    return url.startsWith('https://st-video.cc')
      || url.startsWith('https://cdn-video.51sux.com')
      || url.startsWith('https://cdn-hk.51sux.com');
  };

  // 等待视频元素可解码（已加载元数据且可跳转）
  const waitForVideoReady = (videoEl) => new Promise((resolve, reject) => {
    if (videoEl.readyState >= 2 && videoEl.videoWidth && videoEl.duration) {
      resolve();
      return;
    }
    let settled = false;
    const cleanup = () => {
      videoEl.removeEventListener('loadedmetadata', onReady);
      videoEl.removeEventListener('loadeddata', onReady);
      videoEl.removeEventListener('canplay', onReady);
      videoEl.removeEventListener('error', onErr);
    };
    const onReady = () => {
      if (settled) return;
      if (videoEl.videoWidth && videoEl.duration) {
        settled = true;
        cleanup();
        resolve();
      }
    };
    const onErr = () => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error('视频加载失败，无法提取封面'));
    };
    videoEl.addEventListener('loadedmetadata', onReady);
    videoEl.addEventListener('loadeddata', onReady);
    videoEl.addEventListener('canplay', onReady);
    videoEl.addEventListener('error', onErr);
  });

  // 视频转存完成后，抽取首帧作为项目封面（仅在项目尚无封面时）
  const updateProjectThumbnailFromVideo = async (overrideUrl) => {
    if (readOnly) return;
    if (!project || !project.uuid) {
      console.log('VideoTaskNode - 无项目信息，跳过视频封面更新');
      return;
    }
    if (project.thumbnail_url) {
      console.log('VideoTaskNode - 项目已有封面，跳过视频首帧封面更新');
      return;
    }
    const url = overrideUrl || videoUrl;
    if (!url || !isVideoCorsSafe(url)) {
      console.log('VideoTaskNode - 视频不支持 CORS，跳过首帧封面更新');
      return;
    }
    try {
      const userData = LocalDataService.load_user_data();
      if (!userData || !userData.token) {
        console.log('VideoTaskNode - 未登录，跳过视频封面更新');
        return;
      }

      // 创建独立 video 元素抽帧，避免与展示用 video 抢状态/资源，并确保 crossOrigin 在加载前设置
      const extractEl = document.createElement('video');
      extractEl.crossOrigin = 'anonymous';
      extractEl.muted = true;
      extractEl.playsInline = true;
      extractEl.preload = 'auto';
      extractEl.style.cssText = 'position:fixed;left:-10000px;top:-10000px;width:1px;height:1px;opacity:0;pointer-events:none;';
      document.body.appendChild(extractEl);
      extractEl.src = url;

      try {
        await waitForVideoReady(extractEl);
        const frameBlob = await extractFrameFromVideo(extractEl, 0.1);
        const uploadResult = await uploadFrameToR2(frameBlob, 'first');
        await apiService.updateDrawProject(
          userData.token,
          project.uuid,
          { thumbnail_url: uploadResult.public_url }
        );
        console.log('VideoTaskNode - 已使用视频首帧更新项目封面');
      } finally {
        if (extractEl.parentNode) extractEl.parentNode.removeChild(extractEl);
      }
    } catch (err) {
      console.error('VideoTaskNode - 更新视频首帧封面失败:', err);
    }
  };

  // 启动轮询
  useEffect(() => {
    mountedRef.current = true;

    if (!taskId) return;

    // 如果任务还在进行中，启动快速轮询（5秒）
    if (status === 'pending' || status === 'processing') {
      if (!pollingIntervalRef.current) {
        console.log(`VideoTaskNode ${id} - 启动任务状态轮询（5秒）`);
        hasCheckedTransferRef.current = false;
        
        // 立即查询一次
        pollTaskStatus();
        
        // 启动定时轮询（每5秒）
        pollingIntervalRef.current = setInterval(() => {
          pollTaskStatus();
        }, 5000);
      }
    }
    // 如果任务已完成，检查是否需要视频转存轮询
    else if (status === 'completed') {
      if (!hasCheckedTransferRef.current && !pollingIntervalRef.current) {
        hasCheckedTransferRef.current = true;
        
        if (checkVideoTransfer(videoUrl)) {
          console.log(`VideoTaskNode ${id} - 页面加载时检测到视频未转存，启动30秒轮询`);
          setIsTransferring(true);
          previousTransferStatusRef.current = false;
          
          // 立即查询一次
          pollTaskStatus();
          
          // 启动慢速轮询（每30秒）
          pollingIntervalRef.current = setInterval(() => {
            pollTaskStatus();
          }, 30000);
        } else {
          console.log(`VideoTaskNode ${id} - 页面加载时检测到视频已转存，无需轮询`);
          setIsTransferring(false);
          previousTransferStatusRef.current = true;

          // 页面加载时若视频已转存且项目尚无封面，尝试用首帧更新封面（每个任务最多一次）
          if (!thumbnailUpdateTriggeredRef.current && !project?.thumbnail_url && isVideoCorsSafe(videoUrl)) {
            thumbnailUpdateTriggeredRef.current = true;
            updateProjectThumbnailFromVideo();
          }
        }
      }
    }

    // 清理函数
    return () => {
      mountedRef.current = false;
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [taskId, status]);

  // 手动刷新
  const handleRefresh = () => {
    if (status !== 'completed' && status !== 'failed') {
      pollTaskStatus();
      toast.info('正在刷新任务状态...');
    }
  };

  // 获取状态标签
  const getStatusBadge = () => {
    const statusConfig = {
      pending: { text: '排队中', className: 'VideoTaskNode-status-pending' },
      processing: { text: '生成中', className: 'VideoTaskNode-status-processing' },
      completed: { text: '已完成', className: 'VideoTaskNode-status-completed' },
      failed: { text: '失败', className: 'VideoTaskNode-status-failed' },
    };

    const config = statusConfig[status] || { text: status, className: '' };
    
    return (
      <span className={`VideoTaskNode-status-badge ${config.className}`}>
        {config.text}
      </span>
    );
  };

  // 获取任务类型标签
  const getTaskTypeLabel = () => {
    if (taskType === 'minimax-h3-video') return '海螺 H3 视频生成';
    if (taskType === 'kling-o3-reference-video') return '可灵 O3 视频生成';
    if (taskType === 'gemini-omni-flash-video') return 'Gemini 极速视频生成';
    if (taskType === 'super-seed2-lite-video') return 'Seedance 2.5 视频生成';
    return taskType === 'image-to-video' ? '图生视频' : '文生视频';
  };

  /**
   * 从视频中提取指定时间点的帧
   * @param {HTMLVideoElement} videoElement - 视频元素
   * @param {number} timeInSeconds - 时间点（秒）
   * @returns {Promise<Blob>} - 图片 Blob 对象
   */
  const extractFrameFromVideo = (videoElement, timeInSeconds) => {
    return new Promise((resolve, reject) => {
      try {
        // 创建 canvas
        const canvas = document.createElement('canvas');
        const videoWidth = videoElement.videoWidth;
        const videoHeight = videoElement.videoHeight;
        
        // 限制最大宽度为1920px以减少文件大小
        const maxWidth = 1920;
        let width = videoWidth;
        let height = videoHeight;
        
        if (width > maxWidth) {
          const scale = maxWidth / width;
          width = maxWidth;
          height = height * scale;
        }
        
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        // 跳转到目标时间点
        const originalTime = videoElement.currentTime;
        videoElement.currentTime = timeInSeconds;
        
        // 等待跳转完成
        const handleSeeked = () => {
          try {
            // 等待一帧确保视频已渲染
            requestAnimationFrame(() => {
              // 绘制当前帧
              ctx.drawImage(videoElement, 0, 0, width, height);
              
              // 转换为 Blob (使用 JPEG 格式减少文件大小)
              canvas.toBlob((blob) => {
                if (blob) {
                  // 恢复原始播放位置
                  videoElement.currentTime = originalTime;
                  resolve(blob);
                } else {
                  reject(new Error('无法生成图片'));
                }
              }, 'image/jpeg', 0.92); // 92% 质量的 JPEG
            });
          } catch (err) {
            reject(err);
          }
          
          // 移除监听器
          videoElement.removeEventListener('seeked', handleSeeked);
        };
        
        const handleError = (err) => {
          videoElement.removeEventListener('seeked', handleSeeked);
          videoElement.removeEventListener('error', handleError);
          reject(err);
        };
        
        videoElement.addEventListener('seeked', handleSeeked);
        videoElement.addEventListener('error', handleError);
        
      } catch (error) {
        reject(error);
      }
    });
  };

  /**
   * 计算目标时间点
   * @param {string} frameType - 帧类型 ('first' | 'middle' | 'last' | 'current')
   * @param {number} duration - 视频时长
   * @param {number} currentTime - 当前播放时间
   * @returns {number} - 目标时间点（秒）
   */
  const calculateTargetTime = (frameType, duration, currentTime) => {
    switch (frameType) {
      case 'first':
        return 0.1; // 避免完全的第一帧可能是黑屏
      case 'middle':
        return duration / 2;
      case 'last':
        return Math.max(0.1, duration - 0.5); // 避免超出范围
      case 'current':
        return currentTime;
      default:
        return 0.1;
    }
  };

  /**
   * 上传提取的帧到 R2
   * @param {Blob} imageBlob - 图片数据
   * @param {string} frameType - 帧类型
   * @returns {Promise<Object>} - 包含 public_url 的结果
   */
  const uploadFrameToR2 = async (imageBlob, frameType) => {
    const fileName = `video_${taskId}_${frameType}_frame_${Date.now()}.jpg`;
    const file = new File([imageBlob], fileName, { type: 'image/jpeg' });
    
    const result = await apiService.uploadImageToR2(file);
    return result;
  };

  /**
   * 处理帧提取
   * @param {string} frameType - 帧类型 ('first' | 'middle' | 'last' | 'current')
   */
  const handleExtractFrame = async (frameType) => {
    if (!videoRef.current || readOnly) return;
    
    try {
      setExtracting(true);
      setExtractingType(frameType);
      
      const videoElement = videoRef.current;
      const duration = videoElement.duration;
      const currentTime = videoElement.currentTime;
      
      // 检查视频时长
      if (!duration || duration === 0) {
        toast.error('无法获取视频时长');
        return;
      }
      
      // 计算目标时间点
      const targetTime = calculateTargetTime(frameType, duration, currentTime);
      
      // 提取帧
      toast.info('正在提取视频帧...');
      const frameBlob = await extractFrameFromVideo(videoElement, targetTime);
      
      // 上传到 R2
      toast.info('正在上传图片...');
      const uploadResult = await uploadFrameToR2(frameBlob, frameType);
      
      // 创建 ImageReferenceNode
      const currentNode = getNode(id);
      const newNodeId = nanoid();
      
      const frameTypeLabel = {
        first: '首帧',
        middle: '中间帧',
        last: '尾帧',
        current: '当前帧'
      }[frameType] || frameType;
      
      const newNode = {
        id: newNodeId,
        type: 'imageReference',
        position: {
          x: currentNode.position.x + 450, // 在视频节点右侧450px
          y: currentNode.position.y
        },
        data: {
          imageUrl: uploadResult.public_url,
          fileName: `${frameTypeLabel}_${uploadResult.file_key}`,
          fileKey: uploadResult.file_key,
          isUploading: false
        }
      };
      
      // 添加节点到画布
      await addNodesWithEdges([newNode], []);
      
      toast.success(`${frameTypeLabel}提取成功！`);
      
    } catch (error) {
      console.error('帧提取失败:', error);
      const errorMessage = error.message || '帧提取失败，请重试';
      toast.error(errorMessage);
    } finally {
      setExtracting(false);
      setExtractingType(null);
    }
  };

  return (
    <NodeLayout 
      id={id} 
      type="videoTask" 
      title={`${getTaskTypeLabel()}任务`}
      showInputHandle={true}
      showOutputHandle={true}
    >
      <div className="VideoTaskNode-wrapper">
        {/* 任务信息头部 */}
        <div className="VideoTaskNode-header">
          <div className="VideoTaskNode-task-id">
            任务ID: {taskId ? taskId.slice(0, 8) + '...' : 'N/A'}
          </div>
          {getStatusBadge()}
        </div>
        {Number.isFinite(Number(data.chargedPoints)) && (
          <div className="VideoTaskNode-charge">
            已预扣 {Number(data.chargedPoints).toLocaleString('zh-CN')} 积分
          </div>
        )}

        {/* 视频转存提示 */}
        {isTransferring && status === 'completed' && (
          <div className="VideoTaskNode-transfer-hint">
            🔄 视频转存中...
          </div>
        )}

        {/* 输入参数 */}
        {inputParams && Object.keys(inputParams).length > 0 && (
          <div className="VideoTaskNode-params">
            <div className="VideoTaskNode-params-title">参数</div>
            <div className="VideoTaskNode-params-grid">
              {inputParams.model && (
                <div className="VideoTaskNode-param-item">
                  <span className="VideoTaskNode-param-label">模型:</span>
                  <span className="VideoTaskNode-param-value">
                    {inputParams.model.includes('portrait') ? '📱 竖屏' : '🖥️ 横屏'}
                    {inputParams.model.includes('15s') ? ' 15秒' : 
                     inputParams.model.includes('25s') ? ' 25秒' : ' 10秒'}
                  </span>
                </div>
              )}
              {inputParams.img_url && (
                <div className="VideoTaskNode-param-item">
                  <span className="VideoTaskNode-param-label">参考图:</span>
                  <span className="VideoTaskNode-param-value">已提供</span>
                </div>
              )}
            </div>
            {inputParams.prompt && (
              <div className="VideoTaskNode-prompt">
                <div className="VideoTaskNode-prompt-label">提示词:</div>
                <div className="VideoTaskNode-prompt-text">{inputParams.prompt}</div>
              </div>
            )}
          </div>
        )}

        {/* 进行中状态 */}
        {(status === 'pending' || status === 'processing') && (
          <div className="VideoTaskNode-loading">
            <div className="VideoTaskNode-spinner" />
            <div className="VideoTaskNode-loading-text">
              {status === 'pending' ? '任务排队中，请稍候...' : '正在生成视频...'}
            </div>
            <div className="VideoTaskNode-loading-hint">
              视频生成通常需要 1-5 分钟
            </div>
            <button 
              onClick={handleRefresh}
              disabled={readOnly}
              className="VideoTaskNode-refresh-button"
            >
              🔄 刷新状态
            </button>
          </div>
        )}

        {/* 生成成功 */}
        {status === 'completed' && videoUrl && (
          <div className="VideoTaskNode-results">
            <div className="VideoTaskNode-results-title">
              生成结果
            </div>
            <div className="VideoTaskNode-video-container">
              <video 
                ref={videoRef}
                src={videoUrl} 
                controls
                className="VideoTaskNode-video"
                poster={inputParams.img_url || undefined}
                {...(isVideoCorsSafe(videoUrl) ? { crossOrigin: "anonymous" } : {})}
              >
                您的浏览器不支持视频播放
              </video>
              {outputParams?.video && (
                <div className="VideoTaskNode-video-info">
                  {outputParams.video.width && outputParams.video.height && (
                    <span className="VideoTaskNode-video-info-item">
                      📐 {outputParams.video.width} × {outputParams.video.height}
                    </span>
                  )}
                  {outputParams.video.duration && (
                    <span className="VideoTaskNode-video-info-item">
                      ⏱️ {outputParams.video.duration.toFixed(1)}秒
                    </span>
                  )}
                </div>
              )}
              
              {/* 未转存视频的提示 */}
              {!readOnly && !isVideoCorsSafe(videoUrl) && (
                <div className="VideoTaskNode-frame-extract-disabled">
                  <div className="VideoTaskNode-frame-extract-hint">
                    ⏳ 视频转存后可提取视频帧
                  </div>
                </div>
              )}
              
              {/* 帧提取工具栏 - 只对支持 CORS 的视频显示 */}
              {!readOnly && isVideoCorsSafe(videoUrl) && (
                <div className="VideoTaskNode-frame-extract">
                  <div className="VideoTaskNode-frame-extract-title">
                    📸 提取视频帧
                  </div>
                  <div className="VideoTaskNode-frame-extract-buttons">
                    <button
                      onClick={() => handleExtractFrame('first')}
                      disabled={extracting}
                      className={`VideoTaskNode-frame-button ${extracting && extractingType === 'first' ? 'VideoTaskNode-frame-button-active' : ''}`}
                    >
                      {extracting && extractingType === 'first' ? (
                        <span className="VideoTaskNode-frame-button-loading">⏳</span>
                      ) : (
                        '⏮️'
                      )}
                      <span>首帧</span>
                    </button>
                    <button
                      onClick={() => handleExtractFrame('middle')}
                      disabled={extracting}
                      className={`VideoTaskNode-frame-button ${extracting && extractingType === 'middle' ? 'VideoTaskNode-frame-button-active' : ''}`}
                    >
                      {extracting && extractingType === 'middle' ? (
                        <span className="VideoTaskNode-frame-button-loading">⏳</span>
                      ) : (
                        '⏸️'
                      )}
                      <span>中间帧</span>
                    </button>
                    <button
                      onClick={() => handleExtractFrame('last')}
                      disabled={extracting}
                      className={`VideoTaskNode-frame-button ${extracting && extractingType === 'last' ? 'VideoTaskNode-frame-button-active' : ''}`}
                    >
                      {extracting && extractingType === 'last' ? (
                        <span className="VideoTaskNode-frame-button-loading">⏳</span>
                      ) : (
                        '⏭️'
                      )}
                      <span>尾帧</span>
                    </button>
                    <button
                      onClick={() => handleExtractFrame('current')}
                      disabled={extracting}
                      className={`VideoTaskNode-frame-button ${extracting && extractingType === 'current' ? 'VideoTaskNode-frame-button-active' : ''}`}
                    >
                      {extracting && extractingType === 'current' ? (
                        <span className="VideoTaskNode-frame-button-loading">⏳</span>
                      ) : (
                        '📍'
                      )}
                      <span>当前帧</span>
                    </button>
                  </div>
                  {extracting && (
                    <div className="VideoTaskNode-frame-extract-hint">
                      正在提取并上传图片...
                    </div>
                  )}
                </div>
              )}
              
              <a
                href={videoUrl}
                download
                target="_blank"
                rel="noopener noreferrer"
                className="VideoTaskNode-download-link"
              >
                📥 下载视频
              </a>
            </div>
          </div>
        )}

        {/* 失败状态 */}
        {status === 'failed' && (
          <div className="VideoTaskNode-error">
            <div className="VideoTaskNode-error-icon">❌</div>
            <div className="VideoTaskNode-error-text">
              {error || '生成失败，请检查参数后重试'}
            </div>
            <button 
              onClick={handleRefresh}
              disabled={readOnly}
              className="VideoTaskNode-retry-button"
            >
              🔄 重新查询
            </button>
          </div>
        )}

        {/* 耗时信息 */}
        {outputParams && outputParams.timings && (
          <div className="VideoTaskNode-metrics">
            <div className="VideoTaskNode-metrics-title">耗时信息</div>
            <div className="VideoTaskNode-metrics-grid">
              {outputParams.timings.inference && (
                <div className="VideoTaskNode-metric-item">
                  <span className="VideoTaskNode-metric-label">生成时间:</span>
                  <span className="VideoTaskNode-metric-value">
                    {outputParams.timings.inference.toFixed(1)}秒
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </NodeLayout>
  );
});

VideoTaskNode.displayName = 'VideoTaskNode';
